import * as React from "react";
import { useMutation, useQueryClient, type QueryKey } from "@tanstack/react-query";
import { useToast } from "@ozpanel/ui";
import { FormDialog, type FormFieldSpec, type FormValues } from "@ozpanel/kit";

// useCreate — one hook behind every "Add …" button. Handles the dialog, the mutation, the
// error surface (the SERVER's message, inside the form, where the operator can fix the input
// and retry — not a toast that dismisses the context away), invalidation and the success
// toast. Built once so a new create form is a field list, not a component.
export function useCreate<T extends FormValues>(opts: {
  title: string;
  description?: string;
  submitLabel?: string;
  fields: FormFieldSpec[];
  run(values: T): Promise<unknown>;
  invalidates: QueryKey[];
  success(values: T): string;
}) {
  const [open, setOpen] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const qc = useQueryClient();
  const { notify } = useToast();

  const m = useMutation({
    mutationFn: (v: T) => opts.run(v),
    onSuccess: (_r, v) => {
      for (const k of opts.invalidates) void qc.invalidateQueries({ queryKey: k });
      notify({ kind: "success", title: opts.success(v) });
      setOpen(false);
      setError(null);
    },
    onError: (e) => setError(e instanceof Error ? e.message : String(e)),
  });

  const dialog = (
    <FormDialog
      open={open}
      onOpenChange={(o) => { setOpen(o); if (!o) setError(null); }}
      title={opts.title}
      description={opts.description}
      submitLabel={opts.submitLabel}
      fields={opts.fields}
      pending={m.isPending}
      error={error}
      onSubmit={(v) => m.mutate(v as T)}
    />
  );
  return { open: () => setOpen(true), dialog };
}
